import React, { FC, useCallback, useMemo, useState } from 'react';

import { reportEvent } from 'Shared/analytics';
import {
  GetDetailsResponseType,
  ThirdPartyReports,
} from 'QuoteApp/services/endpoints/GetDetails';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';
import { useAppContext, useReportsContext } from 'QuoteApp/services/providers';

import { Report } from '../Report';
import { useReportRunner } from '../useReportRunner';
import {
  getReportStatusMultiple,
  getReportStatus,
  haveAllRequiredReportsRun,
} from '../utils';
import { ReportStatus } from '../types';
import { getDriversWithoutAssignments } from 'QuoteApp/components/VehicleDriverAssignment';
import { DiscoveredDriversModal } from './DiscoveredDriversModal';

export const hasDiscoveredDrivers = (details: GetDetailsResponseType) => {
  return Object.keys(details.drivers).some(
    driverId => details.drivers[driverId].discoveredDriver
  );
};

export const CurrentCarrierReport: FC = () => {
  const { details, refreshDetails, refreshConfig } = useAppContext();
  const {
    acknowledgeDisclosure,
    changeReportInProgress,
    reportInProgress,
    reports,
    reportsDisabled,
  } = useReportsContext();
  const ddReportCommand = reports.driverDiscovery;
  const ccReportCommand = reports.ncfCc;
  const {
    runReport,
    messages,
    showRateChangeModal,
    rateChangeModalData,
    setShowRateChangeModal,
  } = useReportRunner('ncf-cc');

  const driversWithoutAssignments = useMemo(
    () =>
      getDriversWithoutAssignments(
        details.drivers,
        details.vehicleDriverAssignment
      ),
    [details.drivers, details.vehicleDriverAssignment]
  );

  const ccStatus = useMemo(() => {
    if (
      reportInProgress === ThirdPartyReports.DriverDiscovery ||
      reportInProgress === ThirdPartyReports.NcfAndCurrentCarrier
    ) {
      return ReportStatus.Pending;
    }

    if (ddReportCommand && ccReportCommand) {
      return getReportStatusMultiple([
        ddReportCommand.status,
        ccReportCommand.status,
      ]);
    }

    if (ccReportCommand) {
      return getReportStatus(ccReportCommand.status);
    }

    return ReportStatus.Disabled;
  }, [ccReportCommand, ddReportCommand, reportInProgress]);

  const canRunReport = useMemo(() => {
    if (reportInProgress) {
      return false;
    }

    if (
      details.vehicleDriverAssignment !== null &&
      driversWithoutAssignments.length > 0
    ) {
      return false;
    }

    if (
      !haveAllRequiredReportsRun(
        ThirdPartyReports.NcfAndCurrentCarrier,
        reports
      )
    ) {
      return false;
    }

    if (ddReportCommand) {
      return (
        ddReportCommand.status === 'runnable' ||
        (ddReportCommand.status === 'has_run' &&
          ccReportCommand?.status === 'runnable')
      );
    }

    return ccReportCommand?.status === 'runnable';
  }, [
    reportInProgress,
    reports,
    ddReportCommand,
    ccReportCommand?.status,
    details.vehicleDriverAssignment,
    driversWithoutAssignments,
  ]);

  const [showDiscoveredDriversModal, setShowDiscoveredDriversModal] = useState<
    boolean
  >(false);

  const openDiscoveredDriversModal = useCallback(() => {
    if (!showDiscoveredDriversModal) {
      reportEvent('aqui-tpr-discovered-drivers-modal', {
        additionalDriversFound: Object.keys(details.drivers).filter(
          driverId => details.drivers[driverId].discoveredDriver
        ).length,
      });
      setShowDiscoveredDriversModal(true);
    }
  }, [details, showDiscoveredDriversModal]);

  const runReportOnClick = useCallback(
    async (currentDetails: GetDetailsResponseType) => {
      try {
        changeReportInProgress(ThirdPartyReports.NcfAndCurrentCarrier);

        await acknowledgeDisclosure(Disclosures.Reports);

        let currentCcReport = currentDetails.commands.reports.find(
          r => r.reportName === ThirdPartyReports.NcfAndCurrentCarrier
        );

        let currentDriverDiscoveryReport = currentDetails.commands.reports.find(
          r => r.reportName === ThirdPartyReports.DriverDiscovery
        );

        // Run ADD first
        if (currentDriverDiscoveryReport?.status === 'runnable') {
          const newDetails = await runReport(
            currentDriverDiscoveryReport,
            true
          );

          // If stopped, back out
          if (newDetails.status === 'stop') {
            changeReportInProgress(null);
            return;
          }

          // Otherwise, if has ADD drivers, open modal and back out
          if (hasDiscoveredDrivers(newDetails)) {
            openDiscoveredDriversModal();
            return;
          }

          // Otherwise, get the status of the CC report
          currentCcReport = newDetails.commands.reports.find(
            r => r.reportName === ThirdPartyReports.NcfAndCurrentCarrier
          );
        }

        // If CC is runnable, run it
        if (currentCcReport?.status === 'runnable') {
          const newDetails = await runReport(currentCcReport, true);

          // If stopped, back out
          if (newDetails.status === 'stop') {
            changeReportInProgress(null);
            return;
          }
        }

        reportEvent('ncf-cc-success');
        changeReportInProgress(null);
      } catch (e) {
        // If report failed, it might be because product toggled and Application now requires
        // additional data to fill. Thus, refreshing details and config.
        refreshConfig();
        refreshDetails();
        changeReportInProgress(null);
      }
    },
    [
      openDiscoveredDriversModal,
      changeReportInProgress,
      acknowledgeDisclosure,
      runReport,
      refreshDetails,
      refreshConfig,
    ]
  );

  return ccReportCommand ? (
    <>
      <Report
        displayName="Current Carrier Report"
        messages={messages}
        enabled={!reportsDisabled && canRunReport}
        type="ncf-cc"
        status={ccStatus}
        onClick={() => {
          runReportOnClick(details);
        }}
        showRateChangeModal={showRateChangeModal}
        rateChangeModalData={rateChangeModalData || undefined}
        setShowRateChangeModal={setShowRateChangeModal}
      />
      <DiscoveredDriversModal open={showDiscoveredDriversModal} />
    </>
  ) : null;
};
